<template>
  <v-dialog
    :value="value"
    @input="$emit('input', $event)"
    max-width="900px"
    scrollable
  >
    <v-card>
      <v-card-title class="primary white--text">
        <v-icon left color="white">mdi-history</v-icon>
        Storico Rinnovi
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="pa-0">
        <!-- Info Scadenza -->
        <v-alert v-if="scadenza" type="info" dense text class="ma-4 mb-0">
          <div class="d-flex align-center">
            <v-icon :color="scadenza.tipologiaScadenza.coloreHex" class="mr-3">
              {{ scadenza.tipologiaScadenza.iconaMaterial }}
            </v-icon>
            <div>
              <div class="font-weight-bold">{{ scadenza.titolo }}</div>
              <div class="text-caption">{{ scadenza.tipologiaScadenza.nome }}</div>
            </div>
          </div>
        </v-alert>

        <!-- Tabella Storico -->
        <v-data-table
          :headers="headers"
          :items="storico"
          :loading="loading"
          :items-per-page="10"
          :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
          class="elevation-0"
        >
          <!-- Data Scadenza -->
          <template v-slot:item.dataScadenza="{ item }">
            {{ item.dataScadenza | formatDate }}
          </template>

          <!-- Data Pagamento -->
          <template v-slot:item.dataPagamento="{ item }">
            {{ item.dataPagamento | formatDate }}
          </template>

          <!-- Importo Previsto -->
          <template v-slot:item.importoPrevisto="{ item }">
            {{ item.importoPrevisto | formatCurrency }}
          </template>

          <!-- Importo Pagato -->
          <template v-slot:item.importoPagato="{ item }">
            <span :class="getDifferenzaClass(item)">
              {{ item.importoPagato | formatCurrency }}
            </span>
            <v-tooltip v-if="hasDifferenza(item)" bottom>
              <template v-slot:activator="{ on }">
                <v-icon 
                  small 
                  :color="getDifferenzaColor(item)" 
                  class="ml-1"
                  v-on="on"
                >
                  {{ getDifferenzaIcon(item) }}
                </v-icon>
              </template>
              <span>
                {{ getDifferenzaText(item) }}
              </span>
            </v-tooltip>
          </template>

          <!-- Quantità -->
          <template v-slot:item.qta="{ item }">
            {{ item.qta || '-' }}
          </template>

          <!-- Automatico -->
          <template v-slot:item.automatico="{ item }">
            <v-icon small :color="item.automatico ? 'success' : 'grey'">
              {{ item.automatico ? 'mdi-check-circle' : 'mdi-minus-circle' }}
            </v-icon>
          </template>

          <!-- Note -->
          <template v-slot:item.note="{ item }">
            <span v-if="item.note">
              <v-tooltip bottom max-width="300">
                <template v-slot:activator="{ on }">
                  <v-icon small color="info" v-on="on">
                    mdi-note-text
                  </v-icon>
                </template>
                <span>{{ item.note }}</span>
              </v-tooltip>
            </span>
            <span v-else class="grey--text">-</span>
          </template>

          <!-- No data -->
          <template v-slot:no-data>
            <div class="text-center py-8">
              <v-icon size="64" color="grey lighten-1">mdi-history</v-icon>
              <div class="text-h6 mt-4 grey--text">Nessun rinnovo nello storico</div>
            </div>
          </template>

          <!-- Footer statistiche -->
          <template v-slot:body.append v-if="storico.length > 0">
            <tr class="grey lighten-4 font-weight-bold">
              <td colspan="3" class="text-right">Totale:</td>
              <td>{{ totaleImportoPrevisto | formatCurrency }}</td>
              <td>{{ totaleImportoPagato | formatCurrency }}</td>
              <td colspan="3"></td>
            </tr>
          </template>
        </v-data-table>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="$emit('input', false)">Chiudi</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { getScadenzaById, getStoricoRinnovi } from '@/api/api.scadenze.js';

export default {
  name: 'StoricoRinnoviDialog',

  props: {
    value: {
      type: Boolean,
      default: false
    },
    idScadenza: {
      type: Number,
      required: true
    },
    scadenza: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      loading: false,
      storico: [],

      headers: [
        { text: 'ID', value: 'idRinnovo', align: 'center', width: '80' },
        { text: 'Data Scadenza', value: 'dataScadenza', align: 'center' },
        { text: 'Data Pagamento', value: 'dataPagamento', align: 'center' },
        { text: 'Importo Previsto', value: 'importoPrevisto', align: 'right' },
        { text: 'Importo Pagato', value: 'importoPagato', align: 'right' },
        { text: 'Qta', value: 'qta', align: 'center', width: '80' },
        { text: 'Auto', value: 'automatico', align: 'center', width: '80' },
        { text: 'Note', value: 'note', align: 'center', sortable: false, width: '80' }
      ]
    };
  },

  computed: {
    totaleImportoPrevisto() {
      return this.storico.reduce((sum, item) => sum + (item.importoPrevisto || 0), 0);
    },

    totaleImportoPagato() {
      return this.storico.reduce((sum, item) => sum + (item.importoPagato || 0), 0);
    }
  },

  watch: {
    value(val) {
      if (val && this.idScadenza) {
        this.caricaStorico();
      }
    }
  },

  methods: {
    async caricaStorico() {
      this.loading = true;
      try {
        const response = await getStoricoRinnovi(this.idScadenza);
        this.storico = response.data.data || response.data || [];
      } catch (error) {
        console.error('Errore nel caricamento dello storico:', error);
      } finally {
        this.loading = false;
      }
    },

    hasDifferenza(item) {
      const previsto = item.importoPrevisto || 0;
      const pagato = item.importoPagato || 0;
      return Math.abs(previsto - pagato) > 0.01;
    },

    getDifferenzaClass(item) {
      if (!this.hasDifferenza(item)) return '';
      
      const previsto = item.importoPrevisto || 0;
      const pagato = item.importoPagato || 0;
      
      return pagato > previsto ? 'red--text font-weight-bold' : 'green--text font-weight-bold';
    },

    getDifferenzaColor(item) {
      const previsto = item.importoPrevisto || 0;
      const pagato = item.importoPagato || 0;
      
      return pagato > previsto ? 'red' : 'green';
    },

    getDifferenzaIcon(item) {
      const previsto = item.importoPrevisto || 0;
      const pagato = item.importoPagato || 0;
      
      return pagato > previsto ? 'mdi-arrow-up' : 'mdi-arrow-down';
    },

    getDifferenzaText(item) {
      const previsto = item.importoPrevisto || 0;
      const pagato = item.importoPagato || 0;
      const diff = Math.abs(pagato - previsto);
      
      if (pagato > previsto) {
        return `Pagato €${diff.toFixed(2)} in più del previsto`;
      } else {
        return `Risparmiato €${diff.toFixed(2)} rispetto al previsto`;
      }
    }
  },

  filters: {
    formatDate(value) {
      if (!value) return '-';
      const date = new Date(value);
      return date.toLocaleDateString('it-IT');
    },
    formatCurrency(value) {
      if (!value && value !== 0) return '-';
      return new Intl.NumberFormat('it-IT', {
        style: 'currency',
        currency: 'EUR'
      }).format(value);
    }
  }
};
</script>
